/**
 * Pure helpers for Immo 360 import preview update-confirmation selection.
 * Eligible rows are only `will_update` rows present in the current preview
 * payload (which may be a bounded async sample).
 */

export const WILL_UPDATE_STATUS = 'will_update';

export function eligibleWillUpdateRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter(
    (row) => row && row.status === WILL_UPDATE_STATUS && Number.isInteger(row.rowNumber) && row.rowNumber > 0,
  );
}

export function eligibleWillUpdateRowNumbers(rows) {
  return eligibleWillUpdateRows(rows).map((row) => row.rowNumber);
}

/**
 * @param {Iterable<number>} confirmedUpdates
 * @param {number[]} eligibleRowNumbers
 * @returns {'none'|'partial'|'all'}
 */
export function updateSelectionState(confirmedUpdates, eligibleRowNumbers) {
  const eligible = Array.isArray(eligibleRowNumbers) ? eligibleRowNumbers : [];
  if (!eligible.length) return 'none';
  const confirmed = confirmedUpdates instanceof Set ? confirmedUpdates : new Set(confirmedUpdates || []);
  let selected = 0;
  for (const rowNumber of eligible) {
    if (confirmed.has(rowNumber)) selected += 1;
  }
  if (selected === 0) return 'none';
  if (selected === eligible.length) return 'all';
  return 'partial';
}

export function countConfirmedEligibleUpdates(confirmedUpdates, eligibleRowNumbers) {
  const eligible = Array.isArray(eligibleRowNumbers) ? eligibleRowNumbers : [];
  const confirmed = confirmedUpdates instanceof Set ? confirmedUpdates : new Set(confirmedUpdates || []);
  let selected = 0;
  for (const rowNumber of eligible) {
    if (confirmed.has(rowNumber)) selected += 1;
  }
  return selected;
}

/** Immutable: select every eligible update row number. */
export function selectAllEligibleUpdates(eligibleRowNumbers) {
  return new Set(Array.isArray(eligibleRowNumbers) ? eligibleRowNumbers : []);
}

/** Immutable: clear all update confirmations. */
export function clearAllEligibleUpdates() {
  return new Set();
}

/**
 * Toggle a single will_update row. Ready-row exclusions are handled separately.
 * @returns {Set<number>}
 */
export function toggleConfirmedUpdate(confirmedUpdates, rowNumber) {
  const next = new Set(confirmedUpdates instanceof Set ? confirmedUpdates : []);
  if (!Number.isInteger(rowNumber) || rowNumber <= 0) return next;
  if (next.has(rowNumber)) next.delete(rowNumber);
  else next.add(rowNumber);
  return next;
}

/**
 * Drop confirmations that are not in the current eligible preview set.
 * @returns {Set<number>}
 */
export function pruneConfirmedUpdates(confirmedUpdates, eligibleRowNumbers) {
  const eligible = new Set(Array.isArray(eligibleRowNumbers) ? eligibleRowNumbers : []);
  const confirmed = confirmedUpdates instanceof Set ? confirmedUpdates : new Set(confirmedUpdates || []);
  const next = new Set();
  for (const rowNumber of confirmed) {
    if (eligible.has(rowNumber)) next.add(rowNumber);
  }
  return next;
}

/**
 * True when the authoritative willUpdate count exceeds eligible rows available
 * in the current preview payload (bounded async sample).
 */
export function isPartialUpdatePreviewSample(eligibleInPreviewCount, authoritativeWillUpdateCount) {
  const available = Number(eligibleInPreviewCount) || 0;
  const total = Number(authoritativeWillUpdateCount) || 0;
  return total > available;
}

/**
 * Bulk checkbox click: none/partial → select all eligible; all → clear.
 * @returns {Set<number>}
 */
export function applyBulkUpdateSelectionToggle(currentState, eligibleRowNumbers) {
  if (currentState === 'all') return clearAllEligibleUpdates();
  return selectAllEligibleUpdates(eligibleRowNumbers);
}
